import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Scroll-reveal wrapper. Springs up once when it enters the viewport. */
export function Reveal({
  children,
  className,
  delay = 0,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  as?: 'div' | 'section' | 'li' | 'article';
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const reduced = useReducedMotion();
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' });
  // Failsafe: if the observer never fires (odd scroll containers, throttled
  // background tabs, reduced-motion browsers), show everything anyway.
  const [force, setForce] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setForce(true), 1800);
    return () => clearTimeout(t);
  }, []);
  const M = motion[Tag] as typeof motion.div;
  return (
    <M
      ref={ref}
      initial={reduced ? false : { opacity: 0, y: 18 }}
      animate={inView || force || reduced ? { opacity: 1, y: 0 } : undefined}
      transition={{ type: 'spring', stiffness: 220, damping: 28, delay }}
      className={className}
    >
      {children}
    </M>
  );
}

export function Kicker({ children, className, gold }: { children: ReactNode; className?: string; gold?: boolean }) {
  return (
    <p className={cn('mk-kicker', gold ? 'mk-gold' : 'text-ink-muted', className)}>{children}</p>
  );
}

export function Headline({
  children,
  className,
  size = 'lg',
}: {
  children: ReactNode;
  className?: string;
  size?: 'xl' | 'lg' | 'md';
}) {
  const sizes = {
    xl: 'text-[clamp(2.6rem,7vw,5.5rem)] leading-[0.98]',
    lg: 'text-[clamp(2rem,4.6vw,3.4rem)] leading-[1.02]',
    md: 'text-[clamp(1.5rem,3vw,2.2rem)] leading-[1.08]',
  } as const;
  return (
    <h2 className={cn('font-medium tracking-tightest text-balance', sizes[size], className)}>{children}</h2>
  );
}

export function Section({
  children,
  className,
  dark,
  id,
}: {
  children: ReactNode;
  className?: string;
  dark?: boolean;
  id?: string;
}) {
  return (
    <section id={id} className={cn(dark && 'mk-dark', className)}>
      <div className="mx-auto max-w-6xl px-5 sm:px-8">{children}</div>
    </section>
  );
}

export function CtaLink({
  to,
  children,
  variant = 'solid',
  className,
  dark,
}: {
  to: string;
  children: ReactNode;
  variant?: 'solid' | 'ghost' | 'gold';
  className?: string;
  dark?: boolean;
}) {
  const base =
    'group inline-flex h-11 items-center gap-2 rounded-full px-5 text-sm font-medium tracking-tight transition-[transform,background-color,color,border-color] active:scale-[0.98]';
  const styles = {
    solid: dark ? 'bg-bone text-ink hover:bg-white' : 'bg-ink text-bone hover:bg-ink-soft',
    ghost: dark
      ? 'border border-bone/20 text-bone hover:border-bone/50'
      : 'border border-bone-deep text-ink hover:border-ink-muted',
    gold: 'bg-[oklch(0.8_0.13_78)] text-ink hover:brightness-105',
  } as const;
  const external = /^https?:/.test(to);
  const cls = cn(base, styles[variant], className);
  const inner = (
    <>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
    </>
  );
  return external ? (
    <a href={to} className={cls}>
      {inner}
    </a>
  ) : (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  );
}

/** Spec panel — a mono "terminal" block used for feature details. */
export function Spec({
  title,
  rows,
  className,
  dark,
}: {
  title: string;
  rows: Array<[string, string]>;
  className?: string;
  dark?: boolean;
}) {
  return (
    <div
      className={cn(
        'mk-spec rounded-2xl border p-5',
        dark ? 'border-bone/10 bg-white/[0.03]' : 'border-bone-deep bg-surface',
        className,
      )}
    >
      <p className={cn('mk-kicker', dark ? 'text-bone/50' : 'text-ink-muted')}>{title}</p>
      <dl className="mt-3 space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-baseline justify-between gap-4 text-sm">
            <dt className={cn('font-mono text-[11px]', dark ? 'text-bone/55' : 'text-ink-muted')}>{k}</dt>
            <dd className={cn('text-right', dark ? 'text-bone' : 'text-ink')}>{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Hover-tilt bento tile. */
export function Tile({
  to,
  kicker,
  title,
  body,
  children,
  className,
  dark,
}: {
  to?: string;
  kicker: string;
  title: string;
  body: string;
  children?: ReactNode;
  className?: string;
  dark?: boolean;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [t, setT] = useState({ x: 0, y: 0 });
  const inner = (
    <div
      ref={ref}
      onPointerMove={(e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        setT({ x: ((e.clientX - r.left) / r.width - 0.5) * 6, y: ((e.clientY - r.top) / r.height - 0.5) * -6 });
      }}
      onPointerLeave={() => setT({ x: 0, y: 0 })}
      style={{ transform: `perspective(900px) rotateX(${t.y}deg) rotateY(${t.x}deg)` }}
      className={cn(
        'mk-tilt group flex h-full flex-col overflow-hidden rounded-3xl border p-6 transition-colors',
        dark
          ? 'border-bone/10 bg-white/[0.03] hover:border-[oklch(0.8_0.13_78_/_0.5)]'
          : 'border-bone-deep bg-surface hover:border-ink-muted/40',
        className,
      )}
    >
      <p className={cn('mk-kicker', dark ? 'mk-gold' : 'text-ink-muted')}>{kicker}</p>
      <h3 className={cn('mt-3 text-xl font-medium tracking-tight', dark ? 'text-bone' : 'text-ink')}>{title}</h3>
      <p className={cn('mt-2 text-sm leading-relaxed', dark ? 'text-bone/65' : 'text-ink-muted')}>{body}</p>
      {children && <div className="mt-5 flex-1">{children}</div>}
      {to && (
        <span
          className={cn(
            'mt-5 inline-flex items-center gap-1 text-xs font-medium',
            dark ? 'text-bone/80' : 'text-ink',
          )}
        >
          Explore
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      )}
    </div>
  );
  return to ? (
    <Link to={to} className="block h-full">
      {inner}
    </Link>
  ) : (
    inner
  );
}

/** Animated counter for stats. */
export function Counter({ to, suffix = '', prefix = '' }: { to: number; suffix?: string; prefix?: string }) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const inView = useInView(ref, { once: true });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1100;
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / dur);
      setN(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);
  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {n.toLocaleString()}
      {suffix}
    </span>
  );
}

/** Phone bezel around any content. */
export function Phone({ children, className, dark }: { children: ReactNode; className?: string; dark?: boolean }) {
  return (
    <div
      className={cn(
        'relative w-[280px] rounded-[40px] border p-2 shadow-[0_40px_80px_-40px_rgba(10,9,8,0.7)]',
        dark ? 'border-bone/15 bg-[#1a1714]' : 'border-bone-deep bg-ink',
        className,
      )}
    >
      <div className="absolute left-1/2 top-2 z-10 h-4 w-20 -translate-x-1/2 rounded-full bg-black" />
      <div className="relative h-[560px] overflow-hidden rounded-[32px] bg-bone">{children}</div>
    </div>
  );
}
